import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabaseClient } from '../../config/supabase';
import SupportAuditLogView from '../support/SupportAuditLogView';

const DEFAULT_FORM = {
    supportUserId: '',
    reason: '',
    accessMinutes: '60'
};

const STATUS_STYLES = {
    pending: 'bg-yellow-100 text-yellow-800',
    active: 'bg-green-100 text-green-800',
    redeemed: 'bg-blue-100 text-blue-800',
    revoked: 'bg-red-100 text-red-800',
    expired: 'bg-gray-100 text-gray-800'
};

function formatDate(value) {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString();
}

function shortId(value) {
    return value ? `${value.slice(0, 8)}…` : '—';
}

function normalizeGrantId(data) {
    if (Array.isArray(data)) return data[0]?.grant_id || data[0]?.id || data[0] || null;
    return data?.grant_id || data?.id || data || null;
}

function SupportAccessView({ userId, orgId, theme = 'light' }) {
    const dark = theme === 'dark';
    const [activeTab, setActiveTab] = useState('grants');
    const [members, setMembers] = useState([]);
    const [grants, setGrants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [revokingId, setRevokingId] = useState(null);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [form, setForm] = useState(DEFAULT_FORM);

    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);

        let grantsQuery = supabaseClient
            .from('support_access_grants')
            .select('id, org_id, requested_by, approver_user_id, support_user_id, reason, status, created_at, code_expires_at, access_expires_at, redeemed_at, revoked_at')
            .order('created_at', { ascending: false });

        if (orgId) grantsQuery = grantsQuery.eq('org_id', orgId);

        const [directoryResult, grantsResult] = await Promise.all([
            supabaseClient.rpc('org_member_directory'),
            grantsQuery
        ]);

        if (grantsResult.error) {
            setError(grantsResult.error.message);
            setMembers([]);
            setGrants([]);
        } else {
            setMembers(directoryResult.error ? [] : directoryResult.data || []);
            setGrants(grantsResult.data || []);
        }
        setLoading(false);
    }, [orgId]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const resolvedOrgId = orgId || grants[0]?.org_id || members[0]?.org_id || '';
    const membersById = useMemo(
        () => new Map(members.map((member) => [member.id, member])),
        [members]
    );
    const currentMember = membersById.get(userId);
    const requesterLabel = (grant) => {
        const member = membersById.get(grant.requested_by);
        return member?.full_name || member?.email || shortId(grant.requested_by);
    };
    const supportUserLabel = (grant) => shortId(grant.support_user_id);
    const cardClass = 'bg-[var(--surface-raised)] border-[var(--surface-border)]';
    const textClass = dark ? 'text-gray-100' : 'text-gray-900';
    const mutedClass = dark ? 'text-gray-400' : 'text-gray-600';
    const inputClass = dark
        ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400'
        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500';

    const isRevocable = (grant) => {
        const expiresAt = new Date(grant.access_expires_at);
        return ['pending', 'active'].includes(grant.status)
            && !grant.redeemed_at
            && !grant.revoked_at
            && !Number.isNaN(expiresAt.getTime())
            && expiresAt > new Date();
    };

    const requestSupportAccess = async (event) => {
        event.preventDefault();
        setSaving(true);
        setError(null);
        setNotice(null);

        if (!resolvedOrgId) {
            setError('No organization is available for this support request.');
            setSaving(false);
            return;
        }

        const accessMinutes = Number.parseInt(form.accessMinutes, 10);
        const { data, error: createError } = await supabaseClient.rpc('create_support_access_grant', {
            p_org_id: resolvedOrgId,
            p_approver_user_id: userId,
            p_support_user_id: form.supportUserId.trim(),
            p_reason: form.reason,
            p_access_duration_minutes: accessMinutes
        });

        if (createError) {
            setError(createError.message);
            setSaving(false);
            return;
        }

        const grantId = normalizeGrantId(data);
        if (grantId && supabaseClient.functions?.invoke) {
            const { error: emailError } = await supabaseClient.functions.invoke('generate-support-access-code', {
                body: { grantId }
            });
            if (emailError) {
                setNotice(`Support grant was created, but the access-code email was not sent: ${emailError.message}`);
            } else {
                setNotice('Support access grant created. The one-time code has been emailed to the approver.');
            }
        } else {
            setNotice('Support access grant created. Code email delivery will run when the support function is available.');
        }

        setForm(DEFAULT_FORM);
        await loadData();
        setSaving(false);
    };

    const revokeGrant = async (grant) => {
        if (!window.confirm('Revoke this support access grant?')) return;
        setRevokingId(grant.id);
        setError(null);
        setNotice(null);
        const { error: revokeError } = await supabaseClient.rpc('revoke_support_access_grant', {
            p_grant_id: grant.id
        });
        if (revokeError) {
            setError(revokeError.message);
        } else {
            setNotice('Support access grant revoked.');
            await loadData();
        }
        setRevokingId(null);
    };

    return (
        <div className="max-w-7xl mx-auto space-y-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <h2 className={`text-2xl font-bold ${textClass}`}>Support Access</h2>
                    <p className={`mt-1 text-sm ${mutedClass}`}>Request or revoke time-boxed platform support access for this organization.</p>
                </div>
                {currentMember?.role && <span className={`rounded-full px-3 py-1 text-xs font-medium ${dark ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700'}`}>Your role: {currentMember.role}</span>}
            </div>

            {error && <div className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>}
            {notice && <div className="rounded-lg border border-green-300 bg-green-50 px-4 py-3 text-sm text-green-800">{notice}</div>}

            <div className={`rounded-xl border p-5 ${cardClass}`}>
                <h3 className={`font-semibold ${textClass}`}>Request support access</h3>
                <p className={`mt-1 text-sm ${mutedClass}`}>Creates a one-time, time-boxed grant for a platform support user. The support user must already be a platform super-admin.</p>
                <form onSubmit={requestSupportAccess} className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-3">
                    <label className={`block text-sm font-medium ${textClass}`}>
                        Platform support user UUID
                        <input
                            type="text"
                            value={form.supportUserId}
                            onChange={(event) => setForm({ ...form, supportUserId: event.target.value })}
                            required
                            placeholder="00000000-0000-0000-0000-000000000000"
                            className={`mt-1 w-full rounded-lg border px-3 py-2 ${inputClass}`}
                        />
                    </label>
                    <label className={`block text-sm font-medium ${textClass}`}>
                        Access window
                        <select
                            value={form.accessMinutes}
                            onChange={(event) => setForm({ ...form, accessMinutes: event.target.value })}
                            className={`mt-1 w-full rounded-lg border px-3 py-2 ${inputClass}`}
                        >
                            <option value="15">15 minutes</option>
                            <option value="30">30 minutes</option>
                            <option value="60">1 hour</option>
                            <option value="240">4 hours</option>
                            <option value="1440">24 hours</option>
                        </select>
                    </label>
                    <label className={`block text-sm font-medium lg:col-span-3 ${textClass}`}>
                        Reason
                        <textarea
                            value={form.reason}
                            onChange={(event) => setForm({ ...form, reason: event.target.value })}
                            required
                            maxLength={1000}
                            rows={3}
                            placeholder="Briefly describe what support needs to troubleshoot."
                            className={`mt-1 w-full rounded-lg border px-3 py-2 ${inputClass}`}
                        />
                    </label>
                    <div className="lg:col-span-3 flex justify-end">
                        <button
                            type="submit"
                            disabled={saving || !userId || !resolvedOrgId}
                            className="rounded-lg bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700 disabled:opacity-50"
                        >
                            {saving ? 'Requesting...' : 'Request support access'}
                        </button>
                    </div>
                </form>
            </div>

            <div className={`rounded-xl border overflow-hidden ${cardClass}`}>
                <div className={`flex items-center gap-1 px-5 py-1 border-b ${dark ? 'border-gray-700' : 'border-gray-200'}`}>
                    {[
                        { key: 'grants', label: 'Grants' },
                        { key: 'audit', label: 'Audit Log' }
                    ].map((tab) => (
                        <button
                            key={tab.key}
                            type="button"
                            onClick={() => setActiveTab(tab.key)}
                            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                                activeTab === tab.key
                                    ? 'border-orange-500 text-orange-600 dark:text-orange-400'
                                    : `border-transparent ${mutedClass} hover:${textClass}`
                            }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {activeTab === 'audit' ? (
                    <div className="p-5">
                        <SupportAuditLogView orgId={resolvedOrgId || orgId} dark={dark} />
                    </div>
                ) : loading ? (
                    <div className={`p-8 text-sm ${mutedClass}`}>Loading support access grants...</div>
                ) : grants.length === 0 ? (
                    <div className={`p-8 text-sm ${mutedClass}`}>No support access grants are visible for this organization.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className={dark ? 'bg-gray-700/50' : 'bg-gray-50'}>
                                <tr>
                                    <th className={`px-5 py-3 text-left text-xs font-semibold uppercase ${mutedClass}`}>Status</th>
                                    <th className={`px-5 py-3 text-left text-xs font-semibold uppercase ${mutedClass}`}>Requester</th>
                                    <th className={`px-5 py-3 text-left text-xs font-semibold uppercase ${mutedClass}`}>Support user</th>
                                    <th className={`px-5 py-3 text-left text-xs font-semibold uppercase ${mutedClass}`}>Reason</th>
                                    <th className={`px-5 py-3 text-left text-xs font-semibold uppercase ${mutedClass}`}>Created</th>
                                    <th className={`px-5 py-3 text-left text-xs font-semibold uppercase ${mutedClass}`}>Expires</th>
                                    <th className={`px-5 py-3 text-right text-xs font-semibold uppercase ${mutedClass}`}>Actions</th>
                                </tr>
                            </thead>
                            <tbody className={`divide-y ${dark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                                {grants.map((grant) => (
                                    <tr key={grant.id} className={dark ? 'hover:bg-gray-700/40' : 'hover:bg-gray-50'}>
                                        <td className="px-5 py-4">
                                            <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${STATUS_STYLES[grant.status] || STATUS_STYLES.expired}`}>{grant.status}</span>
                                        </td>
                                        <td className={`px-5 py-4 text-sm ${textClass}`}>{requesterLabel(grant)}</td>
                                        <td className={`px-5 py-4 text-sm ${mutedClass}`}>{supportUserLabel(grant)}</td>
                                        <td className={`px-5 py-4 text-sm ${textClass}`}>{grant.reason}</td>
                                        <td className={`px-5 py-4 text-sm ${mutedClass}`}>{formatDate(grant.created_at)}</td>
                                        <td className={`px-5 py-4 text-sm ${mutedClass}`}>{formatDate(grant.access_expires_at)}</td>
                                        <td className="px-5 py-4 text-right">
                                            <button
                                                onClick={() => revokeGrant(grant)}
                                                disabled={!isRevocable(grant) || revokingId === grant.id}
                                                className="text-sm font-medium text-red-600 hover:text-red-700 disabled:text-gray-400 disabled:cursor-not-allowed"
                                            >
                                                {revokingId === grant.id ? 'Revoking...' : 'Revoke'}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

export default SupportAccessView;
